import type { BaseProps } from '../interfaces';
import type { RequiringAction as IRequiringAction } from '@paid-ui/types';

import { forwardRef, useCallback } from 'react';
import dayjs from 'dayjs';

import { Highlight } from '@paid-ui/components/highlight';
import { utc2Mel } from '@paid-ui/utils/datetime';
import {
  formatRequiringActionDescription,
  formatRequiringActionTitle,
} from '@paid-ui/utils/requiring-action';

import { Skeleton } from '../skeleton';
import {
  ActionContainer,
  ActionContent,
  ActionExtraContent,
  ActionIcon,
  ActionParagraph,
  ActionTimestamp,
  ActionTitle,
  DismissButton,
  RequiringActionMenuItem,
  StyledActionButton,
} from './_Base';

export interface RequiringActionProps extends BaseProps {
  data: IRequiringAction;
  disabled?: boolean;
  q?: string;
  onClick?: (data: IRequiringAction) => void;
  onDismiss?: (data: IRequiringAction) => void;
}

export const RequiringActionSkeleton = forwardRef<HTMLButtonElement, BaseProps>((props, ref) => {
  return (
    <StyledActionButton skeleton ref={ref} {...props}>
      <Skeleton width={24} height={24} />
      <ActionContainer>
        <ActionContent>
          <ActionTitle>
            <Skeleton width={180} height={18} />
          </ActionTitle>
          <ActionTimestamp>
            <Skeleton width={70} height={14} />
          </ActionTimestamp>
        </ActionContent>
        <ActionParagraph>
          <Skeleton width={250} height={14} />
        </ActionParagraph>
      </ActionContainer>
    </StyledActionButton>
  );
});

RequiringActionSkeleton.displayName = 'RequiringActionSkeleton';

export const RequiringAction = forwardRef<HTMLDivElement, RequiringActionProps>((props, ref) => {
  const { data: requiringAction, disabled, q, onClick, onDismiss, ...restProps } = props;

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (typeof onClick === 'function') {
      onClick(requiringAction);
    }
  }, [requiringAction, disabled, onClick]);

  const handleDismiss = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.stopPropagation();
      onDismiss?.(requiringAction);
    },
    [requiringAction, onDismiss],
  );

  return (
    <RequiringActionMenuItem
      ref={ref}
      onClick={handleClick}
      data-cy="requiring-action"
      {...restProps}
    >
      <ActionIcon name="next" />
      <ActionContainer>
        <ActionContent>
          <ActionTitle>
            <Highlight match={q}>{formatRequiringActionTitle(requiringAction)}</Highlight>
          </ActionTitle>
          <ActionParagraph>
            <Highlight match={q}>{formatRequiringActionDescription(requiringAction)}</Highlight>
          </ActionParagraph>
        </ActionContent>
        <ActionExtraContent>
          <ActionTimestamp>{utc2Mel(requiringAction.createdTimestamp).fromNow()}</ActionTimestamp>
          {typeof onDismiss === 'function' ? (
            <DismissButton type="button" disabled={disabled} onClick={handleDismiss}>
              Dismiss
            </DismissButton>
          ) : null}
        </ActionExtraContent>
      </ActionContainer>
    </RequiringActionMenuItem>
  );
});

RequiringAction.displayName = 'RequiringAction';
